import React, { useMemo, useEffect, useState } from "react";
import type { ISiteSlim } from "App/Modules/Models";
import type { ISiteVideoStreamsRepository } from "App/Modules/Repositories";
import { useObservable, useObservableState } from "observable-hooks";
import { useTranslation } from "react-i18next";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import { Skeleton } from "@material-ui/lab";
import { ImageList, ImageListItem, Switch, FormControlLabel } from "@material-ui/core";
import { Subject, combineLatest } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import ImageGridList from "../../Shared/Components/GridList/ImageGridList";

import "./_camera-images.scss";

type Props = {
    site: ISiteSlim;
    siteVideoStreamsRepository: ISiteVideoStreamsRepository;
};

export const CameraImages = (props: Props) => {
    const imagesSubject = useMemo(() => new Subject<void>(), []);
    const [showOverlays, setShowOverlays] = useState(true);
    const $images = useObservable(() => {
        return extendWithLoadingAndFail(
            combineLatest(imagesSubject).pipe(
                switchMap(() =>
                    props.siteVideoStreamsRepository.getForSite(props.site.id).pipe(
                        map((response) => {
                            const { data, ...loadingProps } = response;

                            if (data) {
                                const images = data.map((vs) => {
                                    return {
                                        alarmZones: vs.alarmZones,
                                        image: vs.images.latest,
                                    };
                                });

                                return { data: images, ...loadingProps };
                            }

                            return { data: [], ...loadingProps };
                        }),
                    ),
                ),
            ),
        );
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => imagesSubject.next(), []);
    const images = useObservableState($images);

    const { t } = useTranslation();
    return (
        <div className="camera-images">
            <div className="camera-images__form">
                <FormControlLabel
                    control={
                        <Switch
                            checked={showOverlays}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setShowOverlays(event.target.checked);
                            }}
                            name="show-alarm-zone-overlays"
                        />
                    }
                    label={t("Entities.AlarmZone.ToggleVisibility")}
                />
            </div>
            {images.isLoading ? (
                <ImageList rowHeight={300} cols={1} style={{ width: "100%" }}>
                    <ImageListItem>
                        <Skeleton height="300px" animation="wave" variant="rect" />
                    </ImageListItem>
                </ImageList>
            ) : (
                <ImageGridList timeZone={props.site.timeZone} images={images.data} showAlarmZones={showOverlays} />
            )}
        </div>
    );
};
